/*
  The quantity steppers, made comfortable for everyone.

  Dawn's QuantityInput (assets/global.js) does the counting. This adds what
  it leaves out, without changing how it counts:

  - A button Dawn greys out at the minimum or maximum also says so to a
    screen reader: aria-disabled follows Dawn's `disabled` class. It stays
    focusable, so a keyboard user does not lose their place.
  - On the product page the new amount is read out after every tap
    ("Aantal: 2"), and a tap on a greyed-out button says why nothing
    happened ("Minimum van 1"). The cart has its own announcements.
  - The field opens the number keypad, selects its number on focus so a
    new one can be typed straight over it, and puts an empty or impossible
    value back to the nearest amount that can be ordered.

  The words come from the theme's locale files through data attributes on
  the stepper; without them nothing is announced, nothing is invented.
*/
(() => {
  let live;
  function announce(text) {
    if (!text) return;
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.append(live);
    }
    // Clear first, so the same amount twice is still read twice.
    live.textContent = '';
    setTimeout(() => (live.textContent = text), 60);
  }

  const number = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  function setUp(stepper) {
    if (stepper.dataset.hzfQuantity) return;
    stepper.dataset.hzfQuantity = 'ready';
    const input = stepper.querySelector('input');
    const buttons = [...stepper.querySelectorAll('button[name="plus"], button[name="minus"]')];
    if (!input || !buttons.length) return;

    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'off');
    // A tick later, because Safari on iOS places the caret after focus and
    // would undo an immediate selection.
    input.addEventListener('focus', () => setTimeout(() => input.select(), 0));

    const mirror = (button) => {
      const off = button.classList.contains('disabled');
      if (off) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    };
    const observer = new MutationObserver((records) => records.forEach((r) => mirror(r.target)));
    buttons.forEach((button) => {
      mirror(button);
      observer.observe(button, { attributes: true, attributeFilter: ['class'] });
    });

    // An empty field, a zero or a number past the limit goes back to the
    // nearest amount that can be ordered, in the stepper's own steps.
    input.addEventListener('change', () => {
      const min = number(input.min, 1);
      const max = input.max ? number(input.max, Infinity) : Infinity;
      const step = Math.max(1, number(input.step, 1));
      let value = number(input.value, min);
      value = Math.min(Math.max(value, min), max);
      value = min + Math.round((value - min) / step) * step;
      if (value > max) value -= step;
      if (String(value) !== input.value) {
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    const speaks = stepper.dataset.hzfAnnounce;
    if (!speaks) return;
    // Dawn's own handler sits on the button and runs before this one, and
    // it fires the change event itself. So the amount before the tap is
    // taken on the way down, in the capture phase on the stepper, which
    // runs before any listener on the button.
    let before = input.value;
    stepper.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('button[name="plus"], button[name="minus"]')) before = input.value;
      },
      true,
    );
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        requestAnimationFrame(() => {
          if (input.value === before) {
            announce(button.name === 'minus' ? stepper.dataset.hzfAtMin : stepper.dataset.hzfAtMax);
          } else {
            announce(speaks.replace('[quantity]', input.value));
          }
        });
      });
    });
  }

  function start() {
    document.querySelectorAll('quantity-input').forEach(setUp);
    // The cart drawer and the cart page re-render their rows after every
    // change, so new steppers are picked up as they arrive.
    new MutationObserver(() =>
      document.querySelectorAll('quantity-input:not([data-hzf-quantity])').forEach(setUp),
    ).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
